/**
 * #287 — the map settings popup, opened from the gear on the dashboard map (superusers only; the
 * API refuses anyone else). One value for the whole company, kept in the database.
 *
 * A live preview on top follows the unsaved choices; nine rows below, chips (the status chips'
 * look: tap one) but for the wedge defaults; then Reset to defaults (fills the form) and Save (PATCH,
 * refresh the map config so this user's map changes at once, toast, close). Cancel closes without saving.
 * #330 round 2 — the eighth row added: the colour of the camera direction wedges (red by default).
 * #339 — the ninth, right under it: the width and the range a camera without its own is drawn with, two whole-number
 * fields (40 degrees and 10 m by default). A value out of range, or an empty field, keeps Save off with a hint;
 * the draft keeps its last valid number, so the PATCH (the whole set) always carries two numbers.
 */
import * as Dialog from '@radix-ui/react-dialog'
import { useId, useState } from 'react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Loader2, Settings2, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { CameraPinSvg } from '@/components/map/CameraPinSvg'
import { systemSettingsApi } from '@/api/endpoints'
import { queryKeys } from '@/config/queryConfig'
import { useMapSettings } from '@/hooks/useMapSettings'
import { DEFAULT_MAP_SETTINGS, MAP_SETTINGS_CHOICES, PIN_COLOUR_HEX, PIN_SIZE_WIDTH, pinFill, wedgeColour } from '@/lib/mapSettings'
import { FOV_MAX, FOV_MIN, RANGE_MAX_M, RANGE_MIN_M, parseWhole } from '@/lib/mapDirection'
import { cn } from '@/lib/utils'
import { toast } from '@/lib/toast'
import type { MapConfig, MapSettings } from '@/types'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
}

/** #339 — the two wedge defaults: their bounds (the camera's own width and range bounds) and field labels. */
const WEDGE_FIELDS = {
  wedge_fov_degrees: { min: FOV_MIN, max: FOV_MAX, label: 'map.settings.wedgeWidth' },
  wedge_range_m: { min: RANGE_MIN_M, max: RANGE_MAX_M, label: 'map.settings.wedgeRange' },
} as const
type WedgeKey = keyof typeof WEDGE_FIELDS
const WEDGE_KEYS = Object.keys(WEDGE_FIELDS) as WedgeKey[]
/** The two fields as typed; the draft takes a number only when it is valid. */
type WedgeText = Record<WedgeKey, string>
const wedgeTextOf = (s: MapSettings): WedgeText => ({ wedge_fov_degrees: String(s.wedge_fov_degrees), wedge_range_m: String(s.wedge_range_m) })
const parseWedge = (key: WedgeKey, raw: string) => parseWhole(raw, WEDGE_FIELDS[key].min, WEDGE_FIELDS[key].max)

function Chip({ pressed, onClick, children, swatch, disabled }: { pressed: boolean; onClick: () => void; children: ReactNode; swatch?: string; disabled?: boolean }) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'inline-flex h-8 items-center gap-1.5 rounded-full border px-3 text-xs font-medium transition-colors disabled:opacity-60',
        pressed ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-background text-muted-foreground hover:text-foreground',
      )}
    >
      {swatch && <span aria-hidden className="h-3 w-3 shrink-0 rounded-full ring-1 ring-slate-800/70" style={{ backgroundColor: swatch }} />}
      {children}
    </button>
  )
}

/** #339 — with `labelId`, the row is a group named by its label (the wedge defaults' two fields); the chip rows pass none. */
function Row({ label, labelId, children }: { label: string; labelId?: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5" role={labelId ? 'group' : undefined} aria-labelledby={labelId}>
      <span id={labelId} className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</span>
      <div className="flex flex-wrap gap-1.5">{children}</div>
    </div>
  )
}

export function MapSettingsDialog({ open, onOpenChange }: Props) {
  const { t } = useTranslation('cameras')
  const { t: tc } = useTranslation('common')
  const current = useMapSettings()
  const [draft, setDraft] = useState<MapSettings>(current)
  const qc = useQueryClient()
  const set = <K extends keyof MapSettings>(key: K, value: MapSettings[K]) => setDraft((d) => ({ ...d, [key]: value }))
  // #339 — the wedge defaults as typed: an invalid or empty field keeps Save off; the draft keeps its last valid number
  const fieldId = useId()
  const [wedgeText, setWedgeText] = useState<WedgeText>(() => wedgeTextOf(current))
  const wedgeValid = WEDGE_KEYS.every((k) => parseWedge(k, wedgeText[k]) !== undefined)
  const wedgeLabelId = `${fieldId}-wedge-defaults`
  const wedgeHintId = `${fieldId}-wedge-hint`
  const typeWedge = (key: WedgeKey, raw: string) => {
    setWedgeText((w) => ({ ...w, [key]: raw }))
    const n = parseWedge(key, raw)
    if (n !== undefined) set(key, n)
  }
  const reset = () => {
    setDraft(DEFAULT_MAP_SETTINGS)
    setWedgeText(wedgeTextOf(DEFAULT_MAP_SETTINGS))
  }

  const save = useMutation({
    mutationFn: () => systemSettingsApi.setMapSettings(draft),
    onSuccess: (saved: MapSettings) => {
      // The map config carries the settings: put the saved ones straight into the cached copy, so
      // this user's map changes at once without a refetch.
      qc.setQueryData<MapConfig>(queryKeys.mapConfig.active('web'), (old) => (old ? { ...old, map_settings: saved } : old))
      toast.success(t('map.settings.saved'))
      onOpenChange(false)
    },
    onError: () => toast.error(t('map.settings.failed')),
  })

  const width = PIN_SIZE_WIDTH[draft.pin_size]
  const pinProps = { shape: draft.pin_shape, width, offlineMark: draft.offline_mark, showNumber: draft.show_number }
  const onOff = (key: 'show_number' | 'street_view') => (
    <>
      <Chip disabled={save.isPending} pressed={draft[key]} onClick={() => set(key, true)}>{t('map.settings.on')}</Chip>
      <Chip disabled={save.isPending} pressed={!draft[key]} onClick={() => set(key, false)}>{t('map.settings.off')}</Chip>
    </>
  )

  return (
    <Dialog.Root open={open} onOpenChange={save.isPending ? undefined : onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 flex max-h-[90vh] w-[calc(100vw-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 flex-col rounded-xl border border-border bg-background shadow-2xl">
          <div className="flex items-start justify-between gap-3 px-6 pt-6">
            <div>
              <Dialog.Title className="text-lg font-semibold">{t('map.settings.title')}</Dialog.Title>
              <Dialog.Description className="text-sm text-muted-foreground">{t('map.settings.description')}</Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button type="button" aria-label={tc('actions.close')} disabled={save.isPending} className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground">
                <X className="h-4 w-4" />
              </button>
            </Dialog.Close>
          </div>

          <div className="flex-1 space-y-4 overflow-y-auto px-6 py-4">
            {/* Preview: an online pin, an offline pin and a group circle on a satellite-green swatch */}
            <div data-settings-preview className="flex items-end justify-center gap-6 rounded-lg bg-[#52603f] px-4 pb-3 pt-4">
              <div className="flex flex-col items-center gap-1.5">
                <CameraPinSvg isOnline number="27" colour={pinFill(true, draft)} {...pinProps} />
                <span className="text-[10px] font-semibold uppercase tracking-wide text-white/80">{t('map.settings.previewOnline')}</span>
              </div>
              <div className="flex flex-col items-center gap-1.5">
                <CameraPinSvg isOnline={false} number="42" colour={pinFill(false, draft)} {...pinProps} />
                <span className="text-[10px] font-semibold uppercase tracking-wide text-white/80">{t('map.settings.previewOffline')}</span>
              </div>
              <div className="flex flex-col items-center gap-1.5">
                {/* the map's group circle: brand ring whatever the pin colour (index.css .detercam-cluster) */}
                <span data-preview-cluster className="relative flex h-10 w-10 items-center justify-center rounded-full bg-background text-sm font-semibold text-foreground shadow-lg ring-4 ring-primary">
                  3
                  {draft.offline_mark !== 'none' && <span aria-hidden className="absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full bg-red-500 ring-2 ring-background" />}
                </span>
                <span className="text-[10px] font-semibold uppercase tracking-wide text-white/80">{t('map.settings.previewGroup')}</span>
              </div>
            </div>

            <Row label={t('map.settings.shape')}>
              {MAP_SETTINGS_CHOICES.pin_shape.map((v) => <Chip key={v} disabled={save.isPending} pressed={draft.pin_shape === v} onClick={() => set('pin_shape', v)}>{t(`map.settings.shapes.${v}`)}</Chip>)}
            </Row>
            <Row label={t('map.settings.colour')}>
              {MAP_SETTINGS_CHOICES.pin_colour.map((v) => <Chip key={v} disabled={save.isPending} pressed={draft.pin_colour === v} onClick={() => set('pin_colour', v)} swatch={PIN_COLOUR_HEX[v]}>{t(`map.settings.colours.${v}`)}</Chip>)}
            </Row>
            {/* #330 round 2 — the "Pin colour" swatch is the pin colour chosen just above */}
            <Row label={t('map.settings.wedgeColour')}>
              {MAP_SETTINGS_CHOICES.wedge_colour.map((v) => <Chip key={v} disabled={save.isPending} pressed={draft.wedge_colour === v} onClick={() => set('wedge_colour', v)} swatch={wedgeColour({ ...draft, wedge_colour: v })}>{t(`map.settings.wedgeColours.${v}`)}</Chip>)}
            </Row>
            {/* #339 — the width and the range a camera without its own is drawn with (the editors' placeholders) */}
            <Row label={t('map.settings.wedgeDefaults')} labelId={wedgeLabelId}>
              <div className="grid w-full grid-cols-2 gap-2">
                {WEDGE_KEYS.map((k) => (
                  <div key={k} className="space-y-1">
                    <label className="text-xs text-muted-foreground" htmlFor={`${fieldId}-${k}`}>{t(WEDGE_FIELDS[k].label)}</label>
                    <Input
                      id={`${fieldId}-${k}`}
                      type="number"
                      inputMode="numeric"
                      step={1}
                      min={WEDGE_FIELDS[k].min}
                      max={WEDGE_FIELDS[k].max}
                      value={wedgeText[k]}
                      aria-invalid={parseWedge(k, wedgeText[k]) === undefined}
                      aria-describedby={wedgeValid ? undefined : wedgeHintId}
                      disabled={save.isPending}
                      onChange={(e) => typeWedge(k, e.target.value)}
                    />
                  </div>
                ))}
              </div>
              {!wedgeValid && (
                <p id={wedgeHintId} role="alert" className="w-full text-xs text-destructive">
                  {t('map.settings.wedgeDefaultsInvalid', { fovMin: FOV_MIN, fovMax: FOV_MAX, rangeMin: RANGE_MIN_M, rangeMax: RANGE_MAX_M })}
                </p>
              )}
            </Row>
            <Row label={t('map.settings.size')}>
              {MAP_SETTINGS_CHOICES.pin_size.map((v) => <Chip key={v} disabled={save.isPending} pressed={draft.pin_size === v} onClick={() => set('pin_size', v)}>{t(`map.settings.sizes.${v}`)}</Chip>)}
            </Row>
            <Row label={t('map.settings.offlineMark')}>
              {MAP_SETTINGS_CHOICES.offline_mark.map((v) => <Chip key={v} disabled={save.isPending} pressed={draft.offline_mark === v} onClick={() => set('offline_mark', v)}>{t(`map.settings.offlineMarks.${v}`)}</Chip>)}
            </Row>
            <Row label={t('map.settings.number')}>{onOff('show_number')}</Row>
            <Row label={t('map.settings.style')}>
              {MAP_SETTINGS_CHOICES.map_style.map((v) => <Chip key={v} disabled={save.isPending} pressed={draft.map_style === v} onClick={() => set('map_style', v)}>{t(`map.settings.styles.${v}`)}</Chip>)}
            </Row>
            <Row label={t('map.settings.streetView')}>{onOff('street_view')}</Row>
          </div>

          <div className="flex gap-2 border-t border-border px-6 py-4">
            <Button variant="outline" onClick={reset} disabled={save.isPending}>
              {t('map.settings.reset')}
            </Button>
            <Button className="ml-auto" onClick={() => save.mutate()} disabled={save.isPending || !wedgeValid}>
              {save.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('map.settings.save')}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

/** #287 — the gear in the map's top left corner; opens the popup. Rendered for superusers only. */
export function MapSettingsButton() {
  const { t } = useTranslation('cameras')
  const [open, setOpen] = useState(false)
  return (
    <>
      <button
        type="button"
        aria-label={t('map.settings.open')}
        title={t('map.settings.open')}
        onClick={() => setOpen(true)}
        // Google's own control look: white square, soft shadow, beside the Map | Satellite switch
        className="m-2.5 inline-flex h-10 w-10 items-center justify-center rounded-sm bg-white text-slate-700 shadow-[0_1px_4px_rgba(0,0,0,0.3)] hover:text-slate-950"
      >
        <Settings2 className="h-5 w-5" aria-hidden />
      </button>
      {/* mounted only while open, so it always starts from the saved settings */}
      {open && <MapSettingsDialog open onOpenChange={setOpen} />}
    </>
  )
}
